// SETTINGS V2 — Lokácie "show inactive" header control (SETTINGS-V2-LOC-B).
// Deactivated locations are hidden by default; this small eye toggle reveals
// them, with a mono-eyebrow count so the user knows they exist and aren't gone.
// Presentational only — the toggle state + counts live in LocationsPageV2.
// `hiddenCount` is the number of deactivated rows the DEFAULT filter actually
// hides (a disabled parent kept to avoid orphaning active children is NOT
// counted — see LocationsPageV2), so the number equals exactly what the toggle
// reveals. When nothing is hidden the control renders nothing. Inline styles +
// scoped CSS-var tokens + the shared `.btn`/`.eyebrow` atoms. No MUI/sx/dt/
// Tailwind. SEC-REQ-2: no side effects.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";

export interface LocationsInactiveToggleProps {
  showInactive: boolean;
  hiddenCount: number;
  onToggle: (next: boolean) => void;
}

const LocationsInactiveToggle: React.FC<LocationsInactiveToggleProps> = ({
  showInactive,
  hiddenCount,
  onToggle,
}) => {
  const { translate: t } = useTranslation("settings");
  const { isPhone } = useBreakpoint();

  // Nothing hidden → nothing to toggle.
  if (hiddenCount === 0) return null;

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0, minWidth: 0 }}>
      {/* Count hint — mono eyebrow, only while the deactivated rows are hidden. */}
      {!showInactive && (
        <span className="eyebrow" style={{ whiteSpace: "nowrap" }}>
          {t("settings:locations.inactiveHint", { count: hiddenCount })}
        </span>
      )}
      <button
        type="button"
        className="btn btn-ghost"
        aria-pressed={showInactive}
        onClick={() => onToggle(!showInactive)}
        style={{
          height: isPhone ? 44 : 30,
          whiteSpace: "nowrap",
          flexShrink: 0,
          // Subtle "on" affordance when revealed (btn-ghost has no active state).
          ...(showInactive ? { background: "var(--card-2)", color: "var(--ink)" } : null),
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ flexShrink: 0 }}
        >
          <path d="M1 8s2.7-4.7 7-4.7S15 8 15 8s-2.7 4.7-7 4.7S1 8 1 8Z" />
          <circle cx="8" cy="8" r="1.9" />
          {showInactive && <path d="M2.5 2.5 L13.5 13.5" />}
        </svg>
        {showInactive
          ? t("settings:locations.hideInactive")
          : t("settings:locations.showInactive")}
      </button>
    </div>
  );
};

export default LocationsInactiveToggle;
